/**
 * Sistema de Roteamento do MEDICARE
 * Configuração das rotas da aplicação
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { LoadingSpinner } from '../components/LoadingSpinner';

// Páginas públicas
import { LoginPage } from '../pages/auth/LoginPage';
import { ForgotPasswordPage } from '../pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/auth/ResetPasswordPage';

// Páginas privadas
import { DashboardPage } from '../pages/dashboard/DashboardPage';
import { DiagnosticoPage } from '../pages/diagnostico/DiagnosticoPage';
import { UserManagementPage } from '../pages/users/UserManagementPage';
import { ProfilePage } from '../pages/profile/ProfilePage';
import { ConsultationsPage } from '../pages/consultations/ConsultationsPage';
import { PromptsPage } from '../pages/prompts/PromptsPage';
import { ConfigurationsPage } from '../pages/configurations/ConfigurationsPage';
import EndpointsManagementPage from '../pages/EndpointsManagementPage';
import { NotFoundPage } from '../pages/errors/NotFoundPage';
import { AccessDeniedPage } from '../pages/errors/AccessDeniedPage';

// Componente de rota protegida
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles = [] }) => {
  const { isAuthenticated, isLoading, usuario } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Verificar se o usuário tem acesso ao sistema
  if (usuario && !['medico', 'admin'].includes(usuario.tipo_usuario)) {
    // Usuários que não são médicos ou admin não têm acesso
    return <Navigate to="/acesso-negado" replace />;
  }

  if (requiredRoles.length > 0 && usuario && !requiredRoles.includes(usuario.tipo_usuario)) {
    // Redirecionar baseado no tipo de usuário
    const redirectPath = usuario.tipo_usuario === 'medico' ? '/diagnostico' : '/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

// Componente de rota pública (redireciona se autenticado)
interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, usuario } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    // Verificar se o usuário tem acesso ao sistema
    if (usuario && !['medico', 'admin'].includes(usuario.tipo_usuario)) {
      return <Navigate to="/acesso-negado" replace />;
    }
    
    // Redirecionar baseado no tipo de usuário
    const redirectPath = usuario?.tipo_usuario === 'medico' ? '/diagnostico' : '/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

// Componente para redirecionamento inicial baseado no tipo de usuário
const InitialRedirect: React.FC = () => {
  const { usuario } = useAuth();
  
  // Debug: Verificar dados do usuário no redirecionamento
  console.log('🔍 DEBUG REDIRECT - usuario completo:', usuario);
  console.log('🔍 DEBUG REDIRECT - tipo_usuario:', usuario?.tipo_usuario);
  console.log('🔍 DEBUG REDIRECT - tipo:', usuario?.tipo);
  
  // Verificar se o usuário tem acesso ao sistema
  if (usuario && !['medico', 'admin'].includes(usuario.tipo_usuario)) {
    console.log('🔍 DEBUG REDIRECT - Acesso negado para tipo:', usuario.tipo_usuario);
    return <Navigate to="/acesso-negado" replace />;
  }
  
  const redirectPath = usuario?.tipo_usuario === 'medico' ? '/diagnostico' : '/dashboard';
  console.log('🔍 DEBUG REDIRECT - Redirecionando para:', redirectPath);
  return <Navigate to={redirectPath} replace />;
};

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Rotas públicas */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/esqueci-senha" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />
      <Route path="/nova-senha" element={
        <PublicRoute>
          <ResetPasswordPage />
        </PublicRoute>
      } />

      {/* Rotas protegidas */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        {/* Redirecionamento inicial baseado no tipo de usuário */}
        <Route index element={<InitialRedirect />} />
        
        {/* Dashboard */}
        <Route path="dashboard" element={<DashboardPage />} />
        
        {/* Diagnóstico por IA (apenas médicos) */}
        <Route path="diagnostico" element={
          <ProtectedRoute requiredRoles={['medico']}>
            <DiagnosticoPage />
          </ProtectedRoute>
        } />

        {/* Perfil do usuário */}
        <Route path="perfil" element={<ProfilePage />} />

        {/* Gestão de usuários (apenas admin) */}
        <Route path="usuarios" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <UserManagementPage />
          </ProtectedRoute>
        } />

        {/* Gestão de consultas */}
        <Route path="consultas" element={<ConsultationsPage />} />

        {/* Gestão de prompts (médicos e admin) */}
        <Route path="prompts" element={
          <ProtectedRoute requiredRoles={['admin', 'medico']}>
            <PromptsPage />
          </ProtectedRoute>
        } />

        {/* Configurações do sistema (apenas admin) */}
        <Route path="configuracoes" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <ConfigurationsPage />
          </ProtectedRoute>
        } />

        {/* Gestão de endpoints (apenas admin) */}
        <Route path="endpoints" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <EndpointsManagementPage />
          </ProtectedRoute>
        } />
      </Route>

      {/* Página de acesso negado */}
      <Route path="/acesso-negado" element={<AccessDeniedPage />} />
      
      {/* Página 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;